/**
 * Cost of Sales.
 *
 * Winnie's v8 note asked for this screen by name, in the sidebar under Stock Management,
 * and her screenshot showed the shape: four groupings, an Add Expense button, and a row
 * per cost carrying who it was paid to, the amount, the GST, whether it is billed or
 * paid, and the supplier invoice it came off.
 *
 * Her groupings are kept, and one column is added that her own screen does not have:
 * the vehicle. "Record specific to VIN#" is the whole point of the screen, and a cost
 * list that does not say which car a cost belongs to cannot do that.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { User } from '../../types'
import { DateRange, ALL_TIME } from '../../lib/dateRange'
import { errText } from '../../lib/errText'
import ReportRangeFilter from './ReportRangeFilter'
import InvoiceUploadPanel from './InvoiceUploadPanel'
import {
  CostRow, CostInput, ExpenseType, VehicleOption, EMPTY_COST, EXPENSE_TYPES, CATEGORIES,
  categoryLabel, loadCostOfSales, loadVehicles, saveCost, voidCost, restoreCost,
  costOfSalesCsv, validateCost,
} from '../../services/costOfSalesService'
import './CostOfSalesView.css'
import { categoryOf } from '../../lib/costCategories'
import { COST_TYPE_LABEL, CostType } from '../../lib/commission'
import { DuplicateHit, findDuplicates } from '../../services/duplicateService'
import { invoiceFileUrl } from '../../services/supplierInvoiceService'
import { formatAbn, isValidAbn } from '../../lib/abn'
import { lookupAbn, AbrResult } from '../../services/buyerService'
import ContactSavePrompt from '../ContactSavePrompt'
import { toast } from '../Toast'

/* What the person is asked to choose between when the category cannot say which side of the
   sale a cost belongs to. The same list the split-invoice form offers, for the same reason. */
const CHOOSABLE: CostType[] = ['FreightIn', 'FreightOut', 'OtherOutflow', 'Overhead']

const money = (n: number) =>
  '$' + Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtDate = (iso: string | null) => {
  if (!iso) return '-'
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
}

const statusText = (s: string) => (s === 'paid' ? 'Paid' : s === 'void' ? 'Void' : 'Billed')
const tick = (on: boolean) => (on ? 'Yes' : 'No')

function download(name: string, body: string) {
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function CostOfSalesView({ user }: { user: User }) {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  const [rows, setRows] = useState<CostRow[]>([])
  const [vehicles, setVehicles] = useState<VehicleOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [vehicleFilter, setVehicleFilter] = useState('')
  const [search, setSearch] = useState('')
  const [showVoid, setShowVoid] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState<CostInput | null>(null)
  const [saving, setSaving] = useState(false)
  const [formProblems, setFormProblems] = useState<string[]>([])
  /* The row being edited, kept beside the form because the form carries the fields a
     person can change and this carries the one thing they cannot: the document the cost
     came off. */
  const [editRow, setEditRow] = useState<CostRow | null>(null)
  const [abnNote, setAbnNote] = useState('')
  const [abnResult, setAbnResult] = useState<AbrResult | null>(null)
  const [abnBusy, setAbnBusy] = useState(false)
  const [dupes, setDupes] = useState<DuplicateHit[]>([])

  /*
   * Winnie's item 2 asks for duplicate check points on invoice numbers as well as VIN, rego,
   * seller and bank details. The other four are checked on the deal edit panel; this is the
   * one place an invoice number is actually typed, so it is the one place worth checking.
   *
   * It warns and never blocks. A supplier can legitimately reuse a reference, and a form that
   * refuses to save on a maybe is a form people learn to fight. Debounced, because it reads
   * the expense book and nobody needs that on every keystroke.
   */
  useEffect(() => {
    const ref = form?.supplierInvoice || ''
    if (!ref.trim()) {
      setDupes([])
      return
    }
    let live = true
    const t = window.setTimeout(() => {
      findDuplicates({ invoiceRef: ref })
        .then((hits) => {
          if (live) setDupes(hits.filter((h) => h.kind === 'invoice_ref' && h.otherId !== form?.id))
        })
        .catch(() => {
          if (live) setDupes([])
        })
    }, 500)
    return () => {
      live = false
      window.clearTimeout(t)
    }
  }, [form?.supplierInvoice, form?.id])
  const [vinQuery, setVinQuery] = useState('')

  const refresh = useCallback(async () => {
    const fresh = await loadCostOfSales(range)
    setRows(fresh)
  }, [range.from, range.to])

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    Promise.all([loadCostOfSales(range), loadVehicles()])
      .then(([costs, cars]) => {
        if (!live) return
        setRows(costs)
        setVehicles(cars)
      })
      .catch((err) => {
        if (live) setError(errText(err, 'Could not load the cost of sales.'))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [range.from, range.to])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (!showVoid && r.status === 'void') return false
      if (vehicleFilter && r.dealId !== vehicleFilter) return false
      if (!needle) return true
      return (
        r.paidTo.toLowerCase().includes(needle) ||
        r.description.toLowerCase().includes(needle) ||
        r.supplierInvoice.toLowerCase().includes(needle) ||
        r.reference.toLowerCase().includes(needle) ||
        r.vin.toLowerCase().includes(needle) ||
        r.stockNumber.toLowerCase().includes(needle) ||
        r.vehicle.toLowerCase().includes(needle)
      )
    })
  }, [rows, search, vehicleFilter, showVoid])

  const groups = useMemo(
    () => EXPENSE_TYPES.map((t) => ({
      ...t,
      items: visible.filter((r) => r.expenseType === t.value),
    })),
    [visible],
  )

  const live = visible.filter((r) => r.status !== 'void')
  const total = live.reduce((s, r) => s + r.amount, 0)
  const gstTotal = live.reduce((s, r) => s + r.gst, 0)
  const unpaid = live.filter((r) => r.status === 'billed').reduce((s, r) => s + r.amount, 0)
  const carsCovered = new Set(live.filter((r) => r.dealId).map((r) => r.dealId)).size

  /**
   * Open the document this cost came off.
   *
   * The bucket is private, so the link is signed at the moment of the click and opened in
   * a new tab. A failure says so on the screen rather than opening a blank tab, because a
   * blank tab reads as "there is no invoice" when what happened is "the link did not mint".
   */
  const viewInvoice = async (row: CostRow) => {
    try {
      const url = await invoiceFileUrl(row.invoiceFilePath)
      if (!url) {
        setNotice('That cost has no uploaded invoice to open.')
        return
      }
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setNotice(errText(e, 'Could not open the invoice.'))
    }
  }

  /**
   * Fill the supplier name from the ABN, per Winnie's 13 Sep note.
   *
   * Three rules from that note, all deliberate: a failed checksum records nothing; a name
   * already typed is never overwritten, because auto-fill is a default and not a lock; and
   * a lookup that fails or is not configured on this environment leaves the ABN recorded
   * and the name to be typed, rather than blocking the entry.
   */
  const fillSupplierFromAbn = async () => {
    const digits = (form?.supplierAbn || '').replace(/\D/g, '')
    if (!digits) { setAbnNote(''); return }
    if (!isValidAbn(digits)) {
      setAbnNote('That ABN does not pass the checksum.')
      return
    }
    setAbnBusy(true)
    try {
      const r = await lookupAbn(digits)
      setAbnResult(r)
      if (!r.ok) {
        setAbnNote(formatAbn(digits) + ' recorded. ' + r.message)
        return
      }
      if (r.legalName && !(form?.paidTo || '').trim()) {
        set({ paidTo: r.legalName })
        setAbnNote(formatAbn(digits) + ' — ' + r.legalName + '. Change it if the invoice says otherwise.')
      } else {
        setAbnNote(formatAbn(digits) + ' — ' + r.message)
      }
    } catch {
      setAbnNote(formatAbn(digits) + ' recorded. The ABR could not be reached just now.')
    } finally {
      setAbnBusy(false)
    }
  }

  const openNew = () => {
    setFormProblems([])
    setForm({ ...EMPTY_COST, dealId: vehicleFilter || null })
    setEditRow(null)
    setAbnNote('')
    setAbnResult(null)
    setVinQuery(vehicles.find((x) => x.id === vehicleFilter)?.vin || '')
  }

  const openEdit = (r: CostRow) => {
    setVinQuery(vehicles.find((x) => x.id === r.dealId)?.vin || '')
    setFormProblems([])
    setForm({
      id: r.id,
      expenseType: r.expenseType,
      dealId: r.dealId,
      date: r.date || new Date().toISOString().slice(0, 10),
      paidTo: r.paidTo,
      supplierAbn: r.supplierAbn,
      amount: String(r.amount),
      gst: String(r.gst),
      taxInvoice: r.taxInvoice,
      nonAccounting: r.nonAccounting,
      category: r.category || 'other',
      description: r.description,
      supplierInvoice: r.supplierInvoice,
      reference: r.reference,
      poNumber: r.poNumber,
      bankAccount: r.bankAccount,
      paymentMethod: r.paymentMethod,
      bulkPaymentRef: r.bulkPaymentRef,
      paid: r.status === 'paid',
      datePaid: r.datePaid || '',
      capitalised: r.capitalised,
      costType: r.costType,
    })
    setEditRow(r)
    setAbnNote('')
  }


  /* Winnie 7 Sep: "The GST should be auto calculated at 10% based on the Tax invoice
     selection." On a tax invoice the gross amount already includes the GST, so the GST
     component is the gross divided by 11 - not the gross times 0.10, which would overstate
     it. That is the same 1/11th the GST register and the BAS 1A/1B labels use, and it is
     why the amount field is now named Gross Amount.

     The figure is offered, not enforced: a supplier invoice can carry GST-free lines, and
     a field that refuses the real number is worse than one that fills in the usual one. */

  /* Winnie 7 Sep: "Able to enter in the VIN# and auto suggest similar VIN to select."
     The datalist does the suggesting; this turns whatever the user settled on back into a
     vehicle. An exact VIN, stock number or label wins outright. Failing that a SINGLE
     partial match is accepted - two or more is treated as no match at all, because picking
     the first of several would file the cost against the wrong car, which is the one
     mistake a screen whose whole point is "record specific to VIN#" must not make. */
  const matchVehicle = (q: string): VehicleOption | null => {
    const n = q.trim().toLowerCase()
    if (!n) return null
    const eq = (s: string) => s.toLowerCase() === n
    const has = (s: string) => s.toLowerCase().includes(n)
    const exact = vehicles.find((x) => eq(x.vin) || eq(x.stockNumber) || eq(x.label))
    if (exact) return exact
    const near = vehicles.filter((x) => has(x.vin) || has(x.stockNumber) || has(x.label))
    return near.length === 1 ? near[0] : null
  }

  /* Said out loud under the field, so nobody has to infer from a blank box whether the
     typing landed on a car. */
  const vehicleEcho = (q: string, dealId: string | null) => {
    if (!q.trim()) return 'Not against a vehicle'
    const hit = vehicles.find((x) => x.id === dealId)
    if (hit) return hit.label
    const n = q.trim().toLowerCase()
    const near = vehicles.filter(
      (x) =>
        x.vin.toLowerCase().includes(n) ||
        x.stockNumber.toLowerCase().includes(n) ||
        x.label.toLowerCase().includes(n),
    )
    if (near.length > 1) return String(near.length) + ' vehicles match - keep typing'
    return 'No vehicle matches that'
  }
  const gstFromGross = (gross: string) => {
    const n = Number(gross)
    if (!Number.isFinite(n) || n <= 0) return '0'
    return (Math.round((n / 11) * 100) / 100).toFixed(2)
  }
  const set = (patch: Partial<CostInput>) => setForm((f) => (f ? { ...f, ...patch } : f))

  const submit = async () => {
    if (!form) return
    const problems = validateCost(form)
    setFormProblems(problems)
    if (problems.length) return
    try {
      setSaving(true)
      setError(null)
      await saveCost(form, user?.id ?? null)
      await refresh()
      /* Winnie, 15 Sep: this screen's green banner was the pattern; it is now the
         platform-wide toast, saying the same sentence everywhere. */
      toast.saved()
      setForm(null)
    } catch (err) {
      setFormProblems([errText(err, 'Could not save the expense.')])
    } finally {
      setSaving(false)
    }
  }

  const toggleVoid = async (r: CostRow) => {
    try {
      setError(null)
      if (r.status === 'void') await restoreCost(r.id, user?.id ?? null)
      else await voidCost(r.id, user?.id ?? null)
      await refresh()
      setNotice(r.status === 'void' ? 'Expense restored.' : 'Expense voided. It is still on record.')
    } catch (err) {
      setError(errText(err, 'Could not change the expense.'))
    }
  }

  const exportCsv = () => {
    if (!visible.length) {
      setError('There is nothing in the list to export.')
      return
    }
    download('DirectAuto-cost-of-sales-' + new Date().toISOString().slice(0, 10) + '.csv', costOfSalesCsv(visible))
    setNotice('Exported ' + visible.length + ' cost' + (visible.length === 1 ? '' : 's') + '.')
  }

  if (loading) {
    return (
      <div className="cos">
        <div className="cos-loading">Loading cost of sales...</div>
      </div>
    )
  }

  return (
    <div className="cos">
      <div className="cos-head">
        <h1>Cost of Sales</h1>
        <p className="cos-sub">
          Every acquisition and direct cost, recorded against the vehicle it belongs to. Upload a
          supplier invoice and the platform reads what it can off it, or enter one by hand.
        </p>
      </div>

      {notice && (
        <div className="cos-alert cos-ok" onClick={() => setNotice(null)}>
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="cos-alert cos-bad" onClick={() => setError(null)}>
          <span>{error}</span>
        </div>
      )}

      <ReportRangeFilter value={range} onChange={setRange} />

      <div className="cos-toolbar">
        <label className="cos-field">
          <span>Vehicle</span>
          <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}>
            <option value="">All Vehicles</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
        </label>
        <label className="cos-field">
          <span>Search</span>
          <input
            type="text"
            placeholder="Supplier, VIN, invoice, reference"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="cos-check">
          <input type="checkbox" checked={showVoid} onChange={(e) => setShowVoid(e.target.checked)} />
          Show voided
        </label>
        <span className="cos-spacer" />
        <button className="cos-btn" onClick={exportCsv}>Export CSV</button>
        <button className="cos-btn" onClick={() => setUploading((v) => !v)}>
          {uploading ? 'Close upload' : 'Upload an invoice'}
        </button>
        <button className="cos-btn cos-btn-primary" onClick={openNew}>+ Add Expense</button>
      </div>

      {uploading && (
        <div style={{ marginBottom: '1.5rem' }}>
          <InvoiceUploadPanel
            userId={user?.id ?? null}
            onCancel={() => setUploading(false)}
            onSaved={() => {
              setUploading(false)
              setNotice('Invoice recorded against the vehicles.')
              refresh().catch(() => undefined)
            }}
          />
        </div>
      )}

      <div className="cos-tiles">
        <div className="cos-tile">
          <div className="cos-tile-label">Total cost</div>
          <div className="cos-tile-value">{money(total)}</div>
        </div>
        <div className="cos-tile">
          <div className="cos-tile-label">GST</div>
          <div className="cos-tile-value">{money(gstTotal)}</div>
        </div>
        <div className="cos-tile">
          <div className="cos-tile-label">Still to pay</div>
          <div className="cos-tile-value">{money(unpaid)}</div>
        </div>
        <div className="cos-tile">
          <div className="cos-tile-label">Vehicles covered</div>
          <div className="cos-tile-value">{carsCovered}</div>
        </div>
        <div className="cos-tile">
          <div className="cos-tile-label">Entries</div>
          <div className="cos-tile-value">{live.length}</div>
        </div>
      </div>

      {groups.map((g) => (
        <div className="cos-group" key={g.value}>
          <div className="cos-group-head">
            <h2>{g.label === 'General' ? 'General Expenses' : g.label === 'Warranty' ? 'Warranty Expenses' : g.label === 'Government fee' ? 'Government Fees' : 'Incentive Bonuses'}</h2>
            <span className="cos-group-total">
              {g.items.length} entr{g.items.length === 1 ? 'y' : 'ies'} &middot;{' '}
              {money(g.items.filter((r) => r.status !== 'void').reduce((s, r) => s + r.amount, 0))}
            </span>
          </div>
          {g.items.length === 0 ? (
            <div className="cos-none">
              No {g.label.toLowerCase()} entries. Use <strong>+ Add Expense</strong> to record one.
            </div>
          ) : (
            <div className="cos-table-wrap">
              <table className="cos-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Paid To</th>
                    <th>Vehicle</th>
                    {g.value === 'general' && <th>Non-Accounting</th>}
                    <th className="cos-right">Gross Amount</th>
                    <th>Tax Invoice</th>
                    <th className="cos-right">GST</th>
                    <th>Status</th>
                    <th>Date Paid</th>
                    <th>Bank Account</th>
                    <th>Payment Method</th>
                    <th>Expense Category</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th>Supplier Inv #</th>
                    <th>{g.value === 'government_fee' ? 'Bulk Payment #' : 'PO #'}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((r) => (
                    <tr key={r.id} className={r.status === 'void' ? 'cos-voided' : undefined}>
                      <td className="cos-nowrap">{fmtDate(r.date)}</td>
                      <td className="cos-strong">{r.paidTo || '-'}</td>
                      <td>
                        <div>{r.vehicle || '-'}</div>
                        {r.vin && <div className="cos-muted">{r.vin}</div>}
                      </td>
                      {g.value === 'general' && <td>{tick(r.nonAccounting)}</td>}
                      <td className="cos-right cos-nowrap">{money(r.amount)}</td>
                      <td>{tick(r.taxInvoice)}</td>
                      <td className="cos-right cos-nowrap">{money(r.gst)}</td>
                      <td>
                        <span className={'cos-pill cos-pill-' + r.status}>{statusText(r.status)}</span>
                      </td>
                      <td className="cos-nowrap">{fmtDate(r.datePaid)}</td>
                      <td>{r.bankAccount || '-'}</td>
                      <td>{r.paymentMethod || '-'}</td>
                      <td>{categoryLabel(r.category)}</td>
                      <td>{r.reference || '-'}</td>
                      <td>{r.description || '-'}</td>
                      <td>{r.supplierInvoice || '-'}</td>
                      <td>{(g.value === 'government_fee' ? r.bulkPaymentRef : r.poNumber) || '-'}</td>
                      <td className="cos-nowrap">
                        <button className="cos-btn cos-btn-small" onClick={() => openEdit(r)}>Edit</button>{' '}
                        {r.invoiceFilePath && (
                          <>
                            <button
                              className="cos-btn cos-btn-small"
                              onClick={() => void viewInvoice(r)}
                              title={'Open ' + (r.invoiceFileName || 'the uploaded invoice')}
                            >
                              View invoice
                            </button>{' '}
                          </>
                        )}
                        <button
                          className="cos-btn cos-btn-small cos-btn-danger"
                          onClick={() => toggleVoid(r)}
                          title={r.status === 'void' ? 'Put this cost back' : 'Take this cost out of the totals, keeping the record'}
                        >
                          {r.status === 'void' ? 'Restore' : 'Void'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {form && (
        <div className="cos-modal-back" onClick={() => (saving ? undefined : setForm(null))}>
          <div className="cos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cos-modal-head">
              <h3>{form.id ? 'Edit Expense' : 'Add New Expense'}</h3>
              <button className="cos-close" onClick={() => setForm(null)} aria-label="Close">&times;</button>
            </div>
            <div className="cos-modal-body">
              {formProblems.length > 0 && (
                <div className="cos-alert cos-bad">
                  <ul>
                    {formProblems.map((p, i) => (<li key={i}>{p}</li>))}
                  </ul>
                </div>
              )}
              {dupes.length > 0 && (
                <div className="cos-alert cos-warn">
                  <ul>
                    {dupes.map((h) => (
                      <li key={h.kind + h.otherId}>{h.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="cos-form-grid">
                <label>
                  <span>Order Date</span>
                  <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
                </label>
                <label>
                  <span>Expense Type</span>
                  <select value={form.expenseType} onChange={(e) => set({ expenseType: e.target.value as ExpenseType })}>
                    {EXPENSE_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </label>
                <label>
                  <span>Expense Category</span>
                  <select value={form.category} onChange={(e) => set({ category: e.target.value })}>
                    {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </label>
              {(() => {
                const cat = categoryOf(form.category)
                if (!cat || cat.costType !== null) return null
                return (
                  <label>
                    <span>
                      Which side of the sale <span className="cos-req">required</span>
                    </span>
                    <select
                      value={form.costType || ''}
                      onChange={(e) => set({ costType: (e.target.value || null) as CostType | null })}
                    >
                      <option value="">Freight in, or freight out?</option>
                      {CHOOSABLE.map((c) => (
                        <option key={c} value={c}>
                          {COST_TYPE_LABEL[c]}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              })()}
                <label className="cos-wide">
                  <span>
                    Vehicle{' '}
                    {(form.expenseType === 'general' || form.expenseType === 'warranty') && (
                      <span className="cos-req">required</span>
                    )}
                  </span>
                  <input
                    type="text"
                    list="cos-vin-options"
                    placeholder="Type a VIN, stock number or model"
                    value={vinQuery}
                    onChange={(e) => {
                      const q = e.target.value
                      setVinQuery(q)
                      set({ dealId: matchVehicle(q)?.id ?? null })
                    }}
                  />
                  <datalist id="cos-vin-options">
                    {vehicles.map((x) => (
                      <option key={x.id} value={x.vin || x.stockNumber || x.label}>
                        {x.label}
                      </option>
                    ))}
                  </datalist>
                  <span className="cos-vin-echo">{vehicleEcho(vinQuery, form.dealId)}</span>
                </label>
                <label>
                  <span>Supplier / Paid To <span className="cos-req">required</span></span>
                  <input type="text" value={form.paidTo} onChange={(e) => set({ paidTo: e.target.value })} />
                </label>
                {/* Winnie, 13 Sep: the ABN is its own field, never part of the name, and the
                    lookup is a default rather than a lock - both stay editable afterwards. */}
                <label>
                  <span>Supplier ABN</span>
                  <input
                    type="text"
                    value={form.supplierAbn}
                    placeholder="11 digits"
                    onChange={(e) => { set({ supplierAbn: e.target.value }); setAbnNote(''); setAbnResult(null) }}
                    onBlur={() => void fillSupplierFromAbn()}
                  />
                  <span className="cos-vin-echo">{abnBusy ? 'Checking the ABN…' : abnNote}</span>
                  {/* Winnie, 15 Sep: a supplier the register knows and Contacts does not is
                      exactly the record worth keeping — the next invoice from them fills
                      itself. */}
                  <ContactSavePrompt
                    abn={form?.supplierAbn || ''}
                    result={abnResult}
                    type="Supplier"
                    suggest={{ name: form?.paidTo || null }}
                  />
                </label>
                <label>
                  <span>Gross Amount <span className="cos-req">required</span></span>
                  <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set(form.taxInvoice ? { amount: e.target.value, gst: gstFromGross(e.target.value) } : { amount: e.target.value })} />
                </label>
                <label>
                  <span>GST{form.taxInvoice ? ' — 1/11th of the gross amount' : ''}</span>
                  <input type="number" step="0.01" min="0" value={form.gst} onChange={(e) => set({ gst: e.target.value })} />
                </label>
                <label>
                  <span>Supplier Invoice Number <span className="cos-req">required</span></span>
                  <input type="text" value={form.supplierInvoice} onChange={(e) => set({ supplierInvoice: e.target.value })} />
                  {/* Winnie, 13 Sep: the figures on this form should be checkable against the
                      document they came off without leaving the form. Only where there is a
                      document - a hand-typed cost has nothing to open. */}
                  {editRow && editRow.invoiceFilePath && (
                    <span className="cos-vin-echo">
                      <button type="button" className="cos-link" onClick={() => void viewInvoice(editRow)}>
                        View invoice
                      </button>
                      {editRow.invoiceFileName ? ' — ' + editRow.invoiceFileName : ''}
                    </span>
                  )}
                </label>
                <label>
                  <span>Reference</span>
                  <input type="text" value={form.reference} onChange={(e) => set({ reference: e.target.value })} />
                </label>
                <label>
                  <span>{form.expenseType === 'government_fee' ? 'Bulk Government Payment #' : 'PO #'}</span>
                  {form.expenseType === 'government_fee' ? (
                    <input type="text" value={form.bulkPaymentRef} onChange={(e) => set({ bulkPaymentRef: e.target.value })} />
                  ) : (
                    <input type="text" value={form.poNumber} onChange={(e) => set({ poNumber: e.target.value })} />
                  )}
                </label>
                <label>
                  <span>Bank Account</span>
                  <input type="text" value={form.bankAccount} onChange={(e) => set({ bankAccount: e.target.value })} />
                </label>
                <label>
                  <span>Payment Method</span>
                  <input type="text" value={form.paymentMethod} onChange={(e) => set({ paymentMethod: e.target.value })} />
                </label>
                <label className="cos-wide">
                  <span>Description <span className="cos-req">required</span></span>
                  <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} />
                </label>
                {form.paid && (
                  <label>
                    <span>Date Paid</span>
                    <input type="date" value={form.datePaid} onChange={(e) => set({ datePaid: e.target.value })} />
                  </label>
                )}
              </div>

              <div className="cos-checks">
                <label className="cos-check">
                  <input type="checkbox" checked={form.taxInvoice} onChange={(e) => set(e.target.checked ? { taxInvoice: true, gst: gstFromGross(form.amount) } : { taxInvoice: false, gst: String(0) })} />
                  Tax Invoice
                </label>
                <label className="cos-check">
                  <input type="checkbox" checked={form.nonAccounting} onChange={(e) => set({ nonAccounting: e.target.checked })} />
                  Non-Accounting Trans
                </label>
                <label className="cos-check">
                  <input type="checkbox" checked={form.paid} onChange={(e) => set({ paid: e.target.checked })} />
                  Paid
                </label>
                <label className="cos-check" title="Include this cost in the vehicle's landed cost on the month-end stocktake">
                  <input type="checkbox" checked={form.capitalised} onChange={(e) => set({ capitalised: e.target.checked })} />
                  Capitalise to vehicle cost
                </label>
              </div>
            </div>
            <div className="cos-modal-foot">
              <button className="cos-btn" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button className="cos-btn cos-btn-primary" onClick={submit} disabled={saving}>
                {saving ? 'Saving...' : form.id ? 'Save changes' : 'Add expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
